/**
 * Text a person has typed but not saved yet, kept in this tab's
 * sessionStorage so it survives leaving the screen: the browser's Back, a
 * link, another section or category, a reload. The app's router is a
 * BrowserRouter, which cannot hold Back, so keeping the text is what stops
 * it being lost.
 *
 * A draft can be about a student, so it belongs to the person who wrote it.
 * Each one is stored with its writer's uid and read only by that uid: a
 * draft written by anyone else is deleted unread. AuthContext names the
 * signed-in uid (setDraftOwner) and clears every draft on any sign-out
 * (explicit, expired or revoked session, another tab), so none outlives its
 * writer's session on a shared computer. With no one signed in nothing is
 * read or kept.
 *
 * Storage can be unavailable (a private window, a full quota); a draft is
 * then simply not kept.
 */
const PREFIX = 'psh.draft.';

let owner = null;

/** The signed-in uid whose drafts may be read and written; null for none. */
export function setDraftOwner(uid) {
    owner = uid || null;
}

export function readDraft(key) {
    if (!owner) return null;
    try {
        const raw = window.sessionStorage.getItem(PREFIX + key);
        if (raw === null) return null;
        let draft = null;
        try {
            draft = JSON.parse(raw);
        } catch (_err) {
            draft = null;
        }
        if (draft?.uid === owner && typeof draft.text === 'string') {
            return draft.text;
        }
        window.sessionStorage.removeItem(PREFIX + key);
        return null;
    } catch (_err) {
        return null;
    }
}

/**
 * Keeps `value` under `key`; null or undefined removes the draft. An empty
 * string is kept: emptying a saved email template is a real change.
 */
export function writeDraft(key, value) {
    try {
        if (value != null && owner) {
            window.sessionStorage.setItem(
                PREFIX + key,
                JSON.stringify({ uid: owner, text: value })
            );
        } else {
            window.sessionStorage.removeItem(PREFIX + key);
        }
    } catch (_err) {
        // Not kept.
    }
}

export function clearDrafts() {
    try {
        const keys = [];
        for (let i = 0; i < window.sessionStorage.length; i += 1) {
            const key = window.sessionStorage.key(i);
            if (key?.startsWith(PREFIX)) keys.push(key);
        }
        keys.forEach((key) => window.sessionStorage.removeItem(key));
    } catch (_err) {
        // Nothing to clear.
    }
}
